// Tipos del dominio. Sin dependencias externas.
// Union types + `as const` (sin enums) como pide el proyecto.

export const ORACLE_TOKEN_TYPES = ["WORD", "PUNCTUATION"] as const;
export type OracleTokenType = (typeof ORACLE_TOKEN_TYPES)[number];

export type OracleToken = {
  token: string;
  startTime: number; // en segundos (parseado desde "5.040s")
  endTime: number;
  speakerIndex: number;
  confidence: number;
  type: OracleTokenType;
  /**
   * Código ISO del idioma detectado para este token específico.
   * Whisper Large v3 Turbo lo reporta por token (puede mezclar idiomas
   * dentro de la misma reunión). Es opcional porque no todos los modelos
   * lo emiten.
   */
  languageCode?: string;
};

export type OracleTranscription = {
  speakerCount: number;
  tokens: OracleToken[];
  rawTranscription: string;
  confidence: number;
  audioFormat: {
    format: string;
    numberOfChannels: number;
    sampleRateInHz: number;
  };
};

export type SpeakerProfile = {
  index: number;
  name: string;
  role: string;
};

export type AudioSample = {
  speakerIndex: number;
  startTime: number;
  endTime: number;
  text: string;
  avgConfidence: number;
};

/**
 * Turno de habla generado directamente desde los tokens del JSON de OCI.
 * Reemplaza a SrtSegment + EnrichedSrtSegment (eliminados en Fase 18).
 *
 * - `speakerIndex` viene de OCI directamente, sin matching heurístico.
 * - `avgConfidence` es el promedio de confidence de los tokens WORD del
 *   segmento (excluye PUNCTUATION). Usado en ReviewScreen para filtrar
 *   segmentos de baja confianza de transcripción.
 * - `tokens` incluye todos los tokens (WORD + PUNCTUATION) que componen
 *   el segmento, en orden.
 * - `text` es el texto concatenado con manejo correcto de puntuación
 *   (los tokens PUNCTUATION se pegan al anterior sin espacio delante).
 */
export type TokenSegment = {
  index: number;
  startTime: number;    // segundos — del primer token WORD del segmento
  endTime: number;      // segundos — del último token WORD del segmento
  speakerIndex: number; // asignado directamente por OCI, sin matching
  tokens: OracleToken[];
  avgConfidence: number;
  text: string;
};

// ---------------------------------------------------------------------------
// Procesamientos (Fase 12) — modelo del frontend.
// Los tipos del wire (lo que devuelve el Worker) viven en platform/api.ts.
// Acá viven los tipos "interpretados" que usa la UI.
// ---------------------------------------------------------------------------

/**
 * Estados posibles del lifecycle de un procesamiento desde la perspectiva
 * de la UI. Espejo del enum del Worker pero con UN solo valor extra:
 *   - "loading_files": estado interno del frontend cuando ya hay status
 *     "ready" pero estamos descargando JSON vía PAR. No existe en el
 *     backend; vive solo en NewMeetingScreen.
 */
export type MeetingStatus =
  | "uploading"
  | "transcribing"
  | "ready_to_finalize"
  | "ready"
  | "tagged"
  | "failed"
  | "loading_files";

/**
 * Input del usuario al crear un procesamiento.
 *
 * `language` siempre es "auto" o un código ISO short ("es", "en", "pt-BR").
 * En la UI lo mostramos como dropdown con opciones predefinidas, pero el
 * tipo es string para flexibilidad cuando OCI sume idiomas nuevos.
 */
export type CreateMeetingInput = {
  title: string;
  language: string; // "auto" | "es" | "en" | "pt-BR" | ...
  numberOfSpeakers: number;
  file: File;
};

/**
 * Idiomas soportados que mostramos en el dropdown.
 * "auto" es el default; el resto está alineado con lo que Whisper Large v3
 * Turbo soporta bien (~50 idiomas — listamos los frecuentes en el contexto
 * de Trama).
 */
export const SUPPORTED_LANGUAGES = [
  { code: "auto", label: "Detección automática" },
  { code: "es", label: "Español" },
  { code: "en", label: "Inglés" },
  { code: "pt-BR", label: "Portugués (Brasil)" },
  { code: "fr", label: "Francés" },
  { code: "de", label: "Alemán" },
  { code: "it", label: "Italiano" },
] as const;

export const MIN_SPEAKERS = 2;
export const MAX_SPEAKERS = 16;

// ---------------------------------------------------------------------------
// P3 — Análisis estructural (structure.json)
// ---------------------------------------------------------------------------

export type ConfidenceLevel = "high" | "medium" | "low";

export type ActiveParticipant = {
  id: string;
  directoryPersonId?: string | null;
  name: string;
  role?: string | null;
  totalSpeakingSeconds: number;
  interventions: number;
};

export type MentionedPerson = {
  id: string;
  directoryPersonId?: string | null;
  name: string;
  isGroup: boolean;
  firstMentionTimestamp: number;
};

export type SectionType =
  | "intro"
  | "as_is"
  | "to_be"
  | "discussion"
  | "decisions"
  | "tasks"
  | "closing"
  | "other";

export type MeetingSection = {
  id: string;
  title: string;
  type: SectionType;
  start: number;
  end: number;
  mainParticipantIds: string[];
  isRelevant: boolean;
};

export type MentionType =
  | "reference_to_work"
  | "task_assignment"
  | "responsibility"
  | "stakeholder"
  | "informational"
  | "other";

export type Mention = {
  id: string;
  personId: string;
  sectionId: string;
  timestamp: number;
  type: MentionType;
  context: string;
};

export type Decision = {
  id: string;
  text: string;
  timestamp: number;
  sectionId: string;
  proposedById: string | null;
  approvedById: string | "consensus" | null;
  confidence: ConfidenceLevel;
};

export type Task = {
  id: string;
  text: string;
  responsibleId: string | null;
  responsibleName: string;
  deadline: string | null;
  timestamp: number;
  sectionId: string;
  requiresNotification: boolean;
};

export type StructureDoc = {
  schemaVersion: 1;
  generatedAt: string;
  activeParticipants: ActiveParticipant[];
  mentionedPeople: MentionedPerson[];
  sections: MeetingSection[];
  mentions: Mention[];
  decisions: Decision[];
  tasks: Task[];
};
